/**
 * PA appointment-metadata hook (PROD-1280).
 *
 * POST /api/Patients/AppointmentMetaData  body: { appointmentIds: string[] }
 * Response (Flutter parity): [{ data: { isNurseSigned, vacar, appointmentId, ... } }]
 *
 * Returns a Map keyed by appointmentId so the worklist can look up each row's
 * metadata (the "Triaged" status override + the vaccination-admin stripe).
 * Mirrors the Flutter FetchAppointmentMetaDataEvent, which posts the visible
 * appointment ids in one call (no per-row N+1).
 */
import { useQuery } from '@tanstack/react-query';
import { post } from '@/shared/api/api-client';
import type { AppointmentMetaData } from '@/types/appointment-api';

/** Each response element wraps the metadata under `data` (Flutter `e["data"]`). */
interface MetaDataEnvelope {
  data?: AppointmentMetaData;
}

export type AppointmentMetaDataMap = Map<string, AppointmentMetaData>;

/** Defensive: accept both the wrapped `[{data:{…}}]` and a flat `[{…}]` shape. */
export function toAppointmentMetaDataMap(
  rows: Array<MetaDataEnvelope | AppointmentMetaData>,
): AppointmentMetaDataMap {
  return toMap(rows);
}

function toMap(rows: Array<MetaDataEnvelope | AppointmentMetaData>): AppointmentMetaDataMap {
  const map: AppointmentMetaDataMap = new Map();
  for (const row of rows) {
    if (!row || typeof row !== 'object') continue; // tolerate null / malformed entries
    const meta = (row as MetaDataEnvelope).data ?? (row as AppointmentMetaData);
    if (meta?.appointmentId) map.set(meta.appointmentId, meta);
  }
  return map;
}

export function useAppointmentMetaData(appointmentIds: string[]) {
  // Stable key from the sorted id set — re-fetches only when the visible set changes.
  const idsKey = [...appointmentIds].sort().join(',');
  return useQuery({
    queryKey: ['appointment-metadata', idsKey],
    queryFn: async ({ signal }) => {
      const rows = await post<Array<MetaDataEnvelope | AppointmentMetaData>>(
        '/api/Patients/AppointmentMetaData',
        { appointmentIds },
        { signal },
      );
      return toMap(Array.isArray(rows) ? rows : []);
    },
    enabled: appointmentIds.length > 0,
    // Metadata changes rarely within a worklist session; a short cache avoids
    // refetch churn as the user flips buckets.
    staleTime: 30_000,
  });
}
